import firebase from "firebase/compat";
import { getRandomId } from "./Haim/utils";
import { renameFile } from "./fileValidation";
import type { FileOrImagePreview, FileWithId, ImagePreview } from "../@types/listings/imageData";

const IMAGE_EXTENSIONS = [
  "apng",
  "avif",
  "gif",
  "jpg",
  "jpeg",
  "jfif",
  "pjpeg",
  "pjp",
  "png",
  "webp"
] as const;

type ImageExtensions = typeof IMAGE_EXTENSIONS[number];

function isValidImageExtension(extension: string): extension is ImageExtensions {
  const validatedExtension = extension.split(".").pop()?.toLowerCase() as ImageExtensions;
  return IMAGE_EXTENSIONS.includes(validatedExtension);
}

export function isValidImage(name: string, bytes: number): boolean {
  return isValidImageExtension(name) && bytes < 20 * Math.pow(1024, 2);
}

type ImagesData = {
  imagesPreviewData: ImagePreview[];
  selectedImagesData: FileWithId[];
};

export function getImagesData(files: FileList | null, currentFiles?: number): ImagesData | null {
  if (!files || !files.length) return null;

  const singleEditingMode = currentFiles === undefined;

  const rawImages =
    singleEditingMode || !(currentFiles === 20 || files.length > 20 - currentFiles)
      ? Array.from(files).filter(({ name, size }) => isValidImage(name, size))
      : null;

  if (!rawImages || !rawImages.length) return null;

  const imagesId = rawImages.map(({ name }) => ({
    id: getRandomId(20),
    name: name
  }));

  const imagesPreviewData = rawImages.map((image, index) => ({
    id: imagesId[index].id,
    src: URL.createObjectURL(image),
    alt: imagesId[index].name ?? image.name
  }));

  const selectedImagesData = rawImages.map((image, index) =>
    renameFile(image, imagesId[index].id, imagesId[index].name)
  );

  return { imagesPreviewData, selectedImagesData };
}

export async function uploadImages(
  files: FileOrImagePreview[],
  userId: string
): Promise<null | ImagePreview[]> {
  if (!files.length) return null;

  const imagesPreview: ImagePreview[] = await Promise.all(
    files.map(async (file) => {
      if ("src" in file) return file;

      const { id, name: alt } = file;

      const storageRef = firebase.storage().ref(`/real-estate-listings/${userId}/${id}`);
      const uploadTask = storageRef.put(file);

      await uploadTask;

      const src = (await uploadTask.snapshot.ref.getDownloadURL()) as string;

      return { id, alt, src };
    })
  );

  return imagesPreview;
}
